import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import {
  GridRow,
  GridCell,
  ErrorMessagePara,
} from "../../AddEditInvoice.styled";
import SelectedCompany from "./SelectedCompany";
import SelectedSite from "./SelectedSite";
import Recipients from "./Recipients";
import { Wrapper, ColumnsRow } from "./CompanySite.styled";

const ONGOING_SITE_STATUSES = [3, 12, 13, 14];

const CompanySite = ({ t, formData, onFormChange, isEditMode }) => {
  const generalParams = useGetGeneralParameters();
  const { countryList } = generalParams;
  const countryId = formData.countryId?.value;

  const company = formData.company?.value;
  const site = formData.site?.value;
  const recipients = formData.recipients?.value || [];
  const sendByEmail = !!formData.sendByEmail?.value;

  const [companySites, setCompanySites] = useState([]);

  const fetchCompanySites = (companyId) => {
    if (!companyId || !countryId) {
      setCompanySites([]);
      return;
    }
    axios
      .get(
        `/clientInvoices/sites?countries=[${countryId}]&isActive=true&statusesId=${JSON.stringify(
          ONGOING_SITE_STATUSES
        )}&companyId=${companyId}&limit=-1`
      )
      .then((response) => setCompanySites(response.data?.sites || []))
      .catch(() => setCompanySites([]));
  };

  useEffect(() => {
    if (company?.id) fetchCompanySites(company.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, countryId]);

  useEffect(() => {
    if (!sendByEmail || !company?.id || !site?.contacts || isEditMode) return;
    fetchInvoicingContactsAndSelect(company.id, site.contacts);
  }, [sendByEmail, company?.id, site?.id]);

  const fetchInvoicingContactsAndSelect = (companyId, entityContacts) => {
    if (!sendByEmail || !companyId) return;
    if (isEditMode) return;
    if (entityContacts?.length) {
      const mapped = entityContacts
        .filter((c) => c?.contactId || c?.id)
        .map((c) => ({
          ...c,
          id: null,
          contactId: c.contactId,
        }));
      onFormChange("recipients", mapped);
    } else {
      onFormChange("recipients", []);
    }
  };

  const handleSelectCompany = (selectedCompany) => {
    onFormChange("company", selectedCompany);
  };

  const handleRemoveCompany = () => {
    onFormChange("company", null);
    setCompanySites([]);
  };

  const handleSelectSite = (selectedSite) => {
    onFormChange("site", selectedSite);
    onFormChange("delegatedCompany", selectedSite.delegatedCompany ?? null);
    onFormChange("isSubcontracting", selectedSite.isSubcontracting);
    if (!company?.id && selectedSite.company) {
      onFormChange("company", selectedSite.company);
    }
    if (sendByEmail) {
      fetchInvoicingContactsAndSelect(
        selectedSite.company?.id || company?.id,
        selectedSite.contacts
      );
    }
  };

  const handleRemoveSite = () => {
    onFormChange("site", null);
    onFormChange("delegatedCompany", null);
  };

  const handleChangeRecipients = (newRecipients) => {
    onFormChange("recipients", newRecipients);
  };

  const showRecipients = sendByEmail && !!company?.id;

  const companySectionErrors = [
    formData.company?.error,
    formData.recipients?.error,
  ].filter(Boolean);

  return (
    <Wrapper>
      <GridRow background="#008395" borderRadius="5px 5px 0 0px">
        <GridCell fontWeight="700" fontSize="16px">
          {t("CLIENT_INVOICING_SECTION_CLIENT_SITE_INFO")}
        </GridCell>
      </GridRow>
      {companySectionErrors.length > 0 && (
        <GridRow gap="10px" padding="12px 16px 0px">
          {companySectionErrors.map((error, index) => (
            <ErrorMessagePara key={index}>{error}</ErrorMessagePara>
          ))}
        </GridRow>
      )}
      <ColumnsRow column={showRecipients ? "1fr 1fr 1fr" : "1fr 1fr"}>
        <SelectedCompany
          company={company}
          countryId={countryId}
          countryList={countryList}
          onSelectCompany={handleSelectCompany}
          onRemoveCompany={handleRemoveCompany}
          error={formData.company?.error}
        />
        <SelectedSite
          site={site}
          countryId={countryId}
          countryList={countryList}
          companyId={company?.id}
          companySites={companySites}
          onSelectSite={handleSelectSite}
          onRemoveSite={handleRemoveSite}
        />
        {showRecipients && (
          <Recipients
            companyId={company?.id}
            visible={showRecipients}
            selectedContacts={recipients}
            onChangeSelectedContacts={handleChangeRecipients}
          />
        )}
      </ColumnsRow>
    </Wrapper>
  );
};

CompanySite.propTypes = {
  t: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  onFormChange: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
};

CompanySite.defaultProps = {
  isEditMode: false,
};

export default withTranslation()(CompanySite);
